// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState, useEffect, type ReactNode, useRef } from 'react';

import { Popover, Indicator, Button, Stack, type DividerProps, Divider, Flex, ActionIcon } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { emptyChildren } from 'helpers/emptyChildren';
import { useIsMobile } from 'hooks/useIsMobile';

type Props = {
  hasChanges: boolean;
  filtersAreSet: boolean;
  onApplyChanges: () => void;
  onDiscardChanges: () => void;
  children: ReactNode;
  /** @defaultValue true */
  withDivider?: boolean;
};

export const FilterDivider = (props: DividerProps) => (
  <Divider mx="calc(-1 * var(--mantine-spacing-md))" mt={6} {...props} />
);

export const FiltersPopover = ({
  onApplyChanges,
  onDiscardChanges,
  withDivider = true,
  filtersAreSet,
  ...props
}: Props) => {
  const isMobile = useIsMobile();
  const [opened, setOpened] = useState(false);

  const cache = useRef(props);
  useEffect(() => {
    if (opened) cache.current = props;
  }, [opened, props]);
  const { hasChanges, children } = opened ? props : cache.current;

  const [applyButtonText, setApplyButtonText] = useState<'Refresh' | 'Apply filters'>('Refresh');
  useEffect(() => {
    if (opened) setApplyButtonText(hasChanges ? 'Apply filters' : 'Refresh');
  }, [hasChanges, opened]);

  const saveChanges = useRef(false);
  useEffect(() => {
    saveChanges.current = hasChanges;
  }, [hasChanges]);

  if (emptyChildren(props.children)) return null;

  return (
    <Popover
      position="bottom-end"
      opened={opened}
      onChange={setOpened}
      // The closing of the popover triggers the save or discard
      onClose={() => {
        // Add timeout to allow the popover to close while the page rerenders
        // (useful on pages with lots and lots of data)
        setTimeout(() => {
          if (saveChanges.current) onApplyChanges();
          else onDiscardChanges();
        });
      }}
      onOpen={() => (saveChanges.current = hasChanges)}
    >
      <Popover.Target>
        <Indicator disabled={!filtersAreSet}>
          {isMobile ? (
            <ActionIcon variant="light" onClick={() => setOpened((o) => !o)} size="input-sm">
              <EvolveIcon icon="FilterAlt" size="sm" />
            </ActionIcon>
          ) : (
            <Button
              leftSection={<EvolveIcon icon="FilterAlt" size="sm" />}
              variant="light"
              onClick={() => setOpened((o) => !o)}
            >
              Display
            </Button>
          )}
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown w={300} p="md">
        <Stack gap="xs">
          {children}
          {withDivider && <FilterDivider mb={6} />}
          <Flex gap="xs">
            {hasChanges && (
              <Button
                variant="light"
                fullWidth
                onClick={() => {
                  saveChanges.current = false;
                  setOpened(false);
                }}
              >
                Cancel
              </Button>
            )}
            <Button
              fullWidth
              variant={hasChanges ? undefined : 'light'}
              onClick={() => {
                saveChanges.current = true;
                setOpened(false);
              }}
              leftSection={!hasChanges ? <EvolveIcon icon="Refresh" size="sm" /> : undefined}
            >
              {applyButtonText}
            </Button>
          </Flex>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
